import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/site";
import { usePrefersReducedMotion } from "@/context/ThemeContext";

// Scroll-driven build sequence: CAD wireframe draws, then morphs into the finished photo.
export function ScrollBuildSequence() {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const pathLengthVal = useTransform(scrollYProgress, [0.05, 0.45], [0, 1]);
  const photoOpacity = useTransform(scrollYProgress, [0.35, 0.65], [0, 1]);
  const photoClipPath = useTransform(scrollYProgress, [0.35, 0.7], ["inset(0 0 100% 0)", "inset(0 0 0% 0)"]);
  const wireOpacity = useTransform(scrollYProgress, [0.5, 0.7], [1, 0.15]);

  return (
    <section
      ref={ref}
      className="relative border-b border-border bg-background"
      data-testid="scroll-build-sequence"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-4">
          <p className="font-mono-tech text-xs tracking-widest uppercase text-primary mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-primary" /> Blueprint → Reality
          </p>
          <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
            We draw it. Then we build it.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Every MRE project starts as a precise line drawing and ends as something you can walk
            into — or click into. Scroll to watch the wireframe become real.
          </p>
        </div>

        <motion.div className="lg:col-span-8 relative aspect-[16/10] border border-border overflow-hidden">
          {/* Finished photo */}
          <motion.div
            style={reduced ? { opacity: 1 } : { opacity: photoOpacity, clipPath: photoClipPath }}
            className="absolute inset-0"
          >
            <img src={IMAGES.portfolio1} alt="Finished modern build by MRE Developments in Calgary" className="w-full h-full object-cover" loading="lazy" />
          </motion.div>

          {/* Wireframe overlay (SVG line drawing) */}
          <motion.svg
            viewBox="0 0 800 500"
            className="absolute inset-0 w-full h-full"
            style={reduced ? { opacity: 0.2 } : { opacity: wireOpacity }}
            fill="none"
            preserveAspectRatio="none"
          >
            <rect x="0" y="0" width="800" height="500" fill="hsl(0 0% 4%)" opacity="0.55" />
            {[
              "M80 420 L80 180 L260 90 L440 180 L440 420 Z",
              "M440 420 L440 180 L620 110 L720 200 L720 420 Z",
              "M80 180 L260 90 L440 180",
              "M150 420 L150 300 L230 300 L230 420",
              "M320 420 L320 260 L400 260 L400 420",
              "M520 420 L520 280 L600 280 L600 420",
              "M0 420 L800 420",
            ].map((d, i) => (
              <motion.path
                key={i}
                d={d}
                stroke="#FF4500"
                strokeWidth="1.5"
                pathLength={1}
                style={reduced ? { pathLength: 1 } : { pathLength: pathLengthVal }}
              />
            ))}
            {/* dimension marks */}
            <g stroke="#6A6A6A" strokeWidth="1">
              <line x1="80" y1="450" x2="440" y2="450" />
              <line x1="80" y1="444" x2="80" y2="456" />
              <line x1="440" y1="444" x2="440" y2="456" />
            </g>
            <text x="240" y="470" fill="#A1A1AA" fontSize="14" fontFamily="IBM Plex Mono" textAnchor="middle">
              12.4 m
            </text>
          </motion.svg>

          <div className="absolute inset-0 blueprint-grid-fine opacity-20 pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
}
